/**
 * Voluum **set-level** Traffic Distribution AI helpers.
 *
 * Voluum stores the "AI on/off" + calculation method for a *set of paths* on
 * `automationConfiguration` (Voluum UI: "2/2 sets of paths optimized"):
 *  - Flow → default-paths set
 *  - PathGroup → that rule-based set
 *
 * Per-path `autoOptimized` is a different control: per-path offer / lander
 * weight optimization. Do not conflate the two.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import type {
  VoluumAutomationConfiguration,
  VoluumCalculationMethod,
} from '@trafficpulse/shared';

/** Anything that can host a Voluum automationConfiguration block. */
export interface AutomationConfigHost {
  automationConfiguration?: VoluumAutomationConfiguration;
}

function ensureConfig(host: AutomationConfigHost): VoluumAutomationConfiguration {
  if (!host.automationConfiguration) host.automationConfiguration = {};
  return host.automationConfiguration;
}

export function getSetAutoOptimized(host: AutomationConfigHost): boolean {
  return Boolean(host.automationConfiguration?.autoOptimized);
}

export function getSetCalculationMethod(
  host: AutomationConfigHost,
): VoluumCalculationMethod {
  const v = host.automationConfiguration?.calculationMethod;
  return (v ?? 'AUTO') as VoluumCalculationMethod;
}

/**
 * Voluum requires `calculationMethod` when `autoOptimized: true`. When turning
 * the AI off we leave `calculationMethod` in place so users can flip it back
 * on without losing their choice (Voluum tolerates the extra field).
 */
export function setSetAutoOptimized(
  host: AutomationConfigHost,
  next: boolean,
): void {
  const c = ensureConfig(host);
  c.autoOptimized = next;
  if (next && c.calculationMethod === undefined) c.calculationMethod = 'AUTO';
}

export function setSetCalculationMethod(
  host: AutomationConfigHost,
  next: VoluumCalculationMethod,
): void {
  const c = ensureConfig(host);
  c.calculationMethod = next;
}
